import { useRef, useState } from "react";
import { Checkbox, Dialog, FormControl, Text } from "@primer/react";
import { Button } from "../Button/Button.jsx";
import { ClearableTextInput } from "../ClearableTextInput/ClearableTextInput.jsx";
import { usePortfolioValue } from "./PortfolioValue.jsx";
import "./PortfolioValueDialog.css";

export function PortfolioValueDialog({ onClose, returnFocusRef }) {
  const preference = usePortfolioValue();
  const inputRef = useRef(null);
  const [input, setInput] = useState(() => formatPortfolioValueInput(preference.value));
  const [remembered, setRemembered] = useState(preference.value !== null && preference.remembered);
  const value = parsePortfolioValue(input);
  const invalid = input.trim() !== "" && value === null;
  const unchanged = value === preference.value && remembered === preference.remembered;
  const canSave = !invalid && !unchanged;

  function changeValue(event) {
    const rawValue = event.target.value;
    const formattedValue = formatPortfolioValueDraft(rawValue);
    const nextInput = formattedValue ?? rawValue;
    const logicalOffset = rawValue.slice(0, event.target.selectionStart ?? rawValue.length).replaceAll(",", "").length;
    setInput(nextInput);
    if (nextInput.trim() === "") setRemembered(false);
    if (formattedValue !== null) requestAnimationFrame(() => {
      const caret = caretForLogicalOffset(formattedValue, logicalOffset);
      inputRef.current?.setSelectionRange(caret, caret);
    });
  }

  function save() {
    if (!canSave) return;
    preference.savePreference(value, remembered);
    onClose();
  }

  return <Dialog className="portfolio-value-dialog" title="Portfolio value" width="large" initialFocusRef={inputRef} returnFocusRef={returnFocusRef} onClose={onClose}>
    <div className="portfolio-value-dialog-body">
      <Text as="p">This optional value estimates monetary amounts from allocation percentages. It stays outside your portfolio data and is not included in backups, exports, or shared profiles.</Text>
      <FormControl validationStatus={invalid ? "error" : undefined}>
        <FormControl.Label>Value</FormControl.Label>
        <ClearableTextInput ref={inputRef} aria-label="Portfolio value" clearLabel="Clear portfolio value" inputMode="decimal" value={input} onChange={changeValue} onClear={() => { setInput(""); setRemembered(false); }} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); save(); } }} block />
        {invalid ? <FormControl.Validation variant="error">Enter a number greater than zero with up to two decimal places.</FormControl.Validation> : null}
      </FormControl>
      <FormControl layout="horizontal" disabled={value === null}>
        <Checkbox checked={remembered} onChange={(event) => setRemembered(event.target.checked)} />
        <FormControl.Label>Remember</FormControl.Label>
        <FormControl.Caption>Store locally in this browser for this profile.</FormControl.Caption>
      </FormControl>
    </div>
    <Dialog.Footer><Button tone="secondary" onClick={onClose}>Cancel</Button><Button disabled={!canSave} onClick={save}>Save</Button></Dialog.Footer>
  </Dialog>;
}

export function formatPortfolioValueDraft(input) {
  const normalized = input.replaceAll(",", "");
  if (!/^\d*(?:\.\d{0,2})?$/.test(normalized)) return null;
  const [integer, fraction] = normalized.split(".");
  const groupedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return fraction === undefined ? groupedInteger : `${groupedInteger}.${fraction}`;
}

function formatPortfolioValueInput(value) {
  return value === null ? "" : formatPortfolioValueDraft(String(value)) ?? String(value);
}

function parsePortfolioValue(input) {
  const normalized = input.replaceAll(",", "").trim();
  if (!/^\d+(?:\.\d{0,2})?$/.test(normalized)) return null;
  const value = Number(normalized);
  return Number.isFinite(value) && value > 0 ? value : null;
}

function caretForLogicalOffset(input, logicalOffset) {
  if (logicalOffset === 0) return 0;
  let logicalCharacters = 0;
  for (let index = 0; index < input.length; index += 1) {
    if (input[index] !== ",") logicalCharacters += 1;
    if (logicalCharacters === logicalOffset) return index + 1;
  }
  return input.length;
}
